import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../utils/api';

export const PREFERENCE_STATUS_LABELS = {
  preview: '个性化设置',
  loading: '正在读取',
  ready: '已同步',
  saving: '正在保存',
  saved: '已经记住',
  conflict: '需要再确认',
  error: '保存失败',
};

export default function usePreferenceNamespace(namespace, defaults, { enabled = true } = {}) {
  const [values, setValues] = useState(() => ({ ...defaults }));
  const [status, setStatus] = useState(enabled ? 'loading' : 'preview');
  const versionRef = useRef(0);
  const valuesRef = useRef({ ...defaults });
  const aliveRef = useRef(true);
  const saveChainRef = useRef(Promise.resolve());
  const lastSaveRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) {
      setStatus('preview');
      return;
    }
    setStatus('loading');
    try {
      const response = await api.get(`/preferences/${namespace}`);
      if (!aliveRef.current) return;
      const nextValues = { ...defaults, ...(response.data?.data || {}) };
      versionRef.current = Number(response.data?.version) || 0;
      valuesRef.current = nextValues;
      setValues(nextValues);
      setStatus('ready');
    } catch {
      if (aliveRef.current) setStatus('error');
    }
  }, [defaults, enabled, namespace]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const enqueueSave = useCallback((snapshot) => {
    const saveId = ++lastSaveRef.current;
    saveChainRef.current = saveChainRef.current
      .catch(() => {})
      .then(async () => {
        if (aliveRef.current) setStatus('saving');
        try {
          const response = await api.patch(`/preferences/${namespace}`, {
            data: snapshot,
            base_version: versionRef.current,
          });
          versionRef.current = Number(response.data?.version) || versionRef.current;
          if (aliveRef.current && saveId === lastSaveRef.current) setStatus('saved');
        } catch (error) {
          if (error.response?.status === 409 && error.response?.data?.current) {
            versionRef.current = Number(error.response.data.current.version) || 0;
            if (aliveRef.current) setStatus('conflict');
          } else if (aliveRef.current) {
            setStatus('error');
          }
        }
      });
  }, [namespace]);

  const updatePreference = useCallback((key, value) => {
    const nextValues = { ...valuesRef.current, [key]: value };
    valuesRef.current = nextValues;
    setValues(nextValues);
    if (enabled) enqueueSave(nextValues);
  }, [enabled, enqueueSave]);

  const retry = useCallback(() => {
    if (!enabled) return;
    enqueueSave({ ...valuesRef.current });
  }, [enabled, enqueueSave]);

  return {
    values,
    status,
    statusLabel: PREFERENCE_STATUS_LABELS[status],
    updatePreference,
    retry,
    reload: load,
  };
}
